import React from 'react';
import { motion } from 'motion/react';
import { PlayCircle, X } from 'lucide-react';
import { moduleApi, apiFetch } from '../../lib/api';
import { useCloturePolitique, exigeAvantApres, avecAvantApres, avecPaires, pairesObligatoires } from '../../lib/cloturePolitique';
import { PhotosParPaires, appelDirection } from '../ui/PhotosParPaires';
import { PhotoCaptureField } from '../ui/PhotoCaptureField';
import { ConsignesPhotoAvantApres } from '../ui/ConsignesPhotoAvantApres';

// "Commencer la prestation" (2026-09-24, demande d'Emeni) : la photo "avant" se prend à l'arrivée chez le
// client, pas reconstituée de mémoire à la clôture. Facultatif — fermer cette fenêtre et aller directement
// sur "Terminer la prestation" redemande les deux photos d'un coup, comme avant cette fonctionnalité.
export function StartPrestationModal({ appointmentId, onClose, onDone }: { appointmentId: string; onClose: () => void; onDone: () => Promise<void> | void; }) {
    const [photoBefore, setPhotoBefore] = React.useState<string | null>(null);
    const [photosRequises, setPhotosRequises] = React.useState(true);
    // L'établissement a toujours autorité : la politique ne règle ici que les photos demandées.
    const politique = useCloturePolitique(appointmentId, (u: string) => apiFetch(u));
    const exigeAvant = politique ? exigeAvantApres(politique.photos_mode) : photosRequises;
    const afficherPhoto = politique ? avecAvantApres(politique.photos_mode) : true;
    // Photos par type d'article (2026-10-20) : envoyées une à une ; le serveur vérifie l'obligation.
    const parPaires = politique ? avecPaires(politique.photos_mode) : false;
    const [submitting, setSubmitting] = React.useState(false);
    const [error, setError] = React.useState('');

    React.useEffect(() => {
        moduleApi.getPrestationPlanningSettings()
            .then((s: any) => setPhotosRequises(s?.completion_photos_required !== false))
            .catch(() => {});
    }, []);

    const handleValidate = async () => {
        if (!parPaires && exigeAvant && !photoBefore) { setError('La photo avant intervention est obligatoire.'); return; }
        setSubmitting(true);
        setError('');
        try {
            const res: any = await moduleApi.startPrestationAppointment(appointmentId, parPaires ? { photos_paires: true } : { photo_before: photoBefore });
            if (res?.error) { setError(res.error); setSubmitting(false); return; }
            await onDone();
        } catch (e) {
            setError('Erreur lors du démarrage de la prestation.');
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                className="w-full max-w-md bg-surface border border-border rounded-3xl max-h-[85vh] shadow-2xl flex flex-col overflow-hidden">
                <div className="flex items-center justify-between p-5 border-b border-border shrink-0">
                    <h3 className="font-bold text-content flex items-center gap-2"><PlayCircle size={18} className="text-brand-500" /> Commencer la prestation</h3>
                    <button onClick={onClose} className="p-1.5 text-content-muted hover:text-content rounded-lg hover:bg-surface-soft"><X size={18} /></button>
                </div>
                <div className="p-5 space-y-4 overflow-y-auto">
                    {parPaires && politique && (
                        <PhotosParPaires appointmentId={appointmentId} etape="demarrer" appel={appelDirection} obligatoire={pairesObligatoires(politique.photos_mode)} />
                    )}
                    {!parPaires && <p className="text-xs text-content-muted">
                        {!afficherPhoto
                            ? "Aucune photo n'est demandée au démarrage. Les photos d'ensemble se prennent à la fin de la prestation."
                            : exigeAvant
                            ? 'Prenez la photo avant intervention maintenant : elle servira de référence pour la photo après, prise au même endroit, au même angle, à la même lumière.'
                            : 'Photo facultative — si vous la prenez maintenant, elle servira de référence pour la photo après, au même endroit et au même angle.'}
                    </p>}
                    {!parPaires && afficherPhoto && <ConsignesPhotoAvantApres />}
                    {!parPaires && afficherPhoto && <PhotoCaptureField label="Photo avant" value={photoBefore} onChange={setPhotoBefore} />}
                    {error && <p className="text-xs text-red-400 font-semibold">{error}</p>}
                </div>
                <div className="flex justify-end gap-3 p-5 border-t border-border shrink-0">
                    <button onClick={onClose} className="px-4 py-2.5 rounded-xl border border-border text-content-muted hover:text-content text-sm font-bold">Annuler</button>
                    <button onClick={handleValidate} disabled={submitting} className="px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold disabled:opacity-50">
                        {submitting ? 'Démarrage...' : 'Commencer'}
                    </button>
                </div>
            </motion.div>
        </div>
    );
}
